// Custom router utilities to bypass React Router and avoid React duplication issues
import { useEffect } from 'react';

export interface CustomRouteParams {
  projektId?: string;
  folderId?: string;
}

export interface CustomSearchParams {
  accept_share?: string;
}

// Parse URL path parameters
export const parseRouteParams = (): CustomRouteParams => {
  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);
  
  // Handle routes like /uppdrag/projekt/{projektId}/folder/{folderId}
  // or /uppdrag/shared/{folderId}
  const params: CustomRouteParams = {};
  
  if (segments.includes('uppdrag')) {
    const uppdragIndex = segments.indexOf('uppdrag');
    
    // Handle shared folders: /uppdrag/shared/{folderId}
    if (segments[uppdragIndex + 1] === 'shared') {
      params.folderId = segments[uppdragIndex + 2];
    }
    // Handle project routes: /uppdrag/projekt/{projektId}/folder/{folderId}
    else if (segments[uppdragIndex + 1] === 'projekt') {
      params.projektId = segments[uppdragIndex + 2];
      if (segments[uppdragIndex + 3] === 'folder') {
        params.folderId = segments[uppdragIndex + 4];
      }
    }
    // Handle direct project ID: /uppdrag/{projektId}
    else if (segments[uppdragIndex + 1] && segments[uppdragIndex + 1] !== 'shared') {
      params.projektId = segments[uppdragIndex + 1];
    }
  }
  
  return params;
};

// Parse URL search parameters
export const parseSearchParams = (): CustomSearchParams => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    accept_share: urlParams.get('accept_share') || undefined
  };
};

// Custom navigation function
// DEPRECATED: Use react-router-dom's useNavigate() instead
// This function bypasses React Router and causes navigation issues
export const customNavigate = (path: string, options?: { replace?: boolean; state?: any }) => {
  console.warn('⚠️ customNavigate is deprecated. Use useNavigate() from react-router-dom instead.');
  if (options?.replace) {
    window.history.replaceState(options?.state || null, '', path);
  } else {
    window.history.pushState(options?.state || null, '', path);
  }
  
  // Trigger a popstate event to notify components of the navigation
  window.dispatchEvent(new PopStateEvent('popstate', { state: options?.state }));
};

// Hook to listen for route changes
export const useCustomRouteChange = (callback: () => void) => {
  useEffect(() => {
    const handleRouteChange = () => {
      callback();
    };
    
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [callback]);
};